import {
    ActivityProduct,
    ActivityTicket,
    BookingCancellationPolicy,
    HotelProduct,
    HotelRoom,
    MainBookingProduct,
    ProductType,
} from '@/api/MyBooking/myBooking.interfaces';
import {
    convertToDate,
    getDateAddingDuration,
    getDurationInHours,
    isAfter,
} from '@shared/helpers/datetime/datetime';
import { isEmpty } from 'lodash';
import {
    BookingCancellationItem,
    BookingItemCancellationPolicy,
    BookingProductCancellation,
} from './BookingCancellation.interfaces';
import { compare, onDate } from '@shared/utils/comparisons';
import { handleCustomError } from '@/config/errorHandling';

const dateComparer = compare<BookingCancellationPolicy>(
    onDate((x) => convertToDate((x as BookingCancellationPolicy).fromDate))
);

function getToday() {
    return getDateAddingDuration(new Date(), { days: 0 });
}

export function getBookingProductCancellation(
    product: MainBookingProduct,
    policies: BookingCancellationPolicy[]
): BookingProductCancellation {
    const items: BookingCancellationItem[] = getBookingCancellationItems(product, policies);

    return {
        refundable: !isEmpty(items) && items.every((x) => x.refundable),
        items,
    };
}

function getBookingCancellationItems(
    product: MainBookingProduct,
    policies: BookingCancellationPolicy[]
): BookingCancellationItem[] {
    switch (product.type) {
        case ProductType.Hotel:
            return getHotelItems(product as HotelProduct, policies);
        case ProductType.Activity:
            return getActivityItems(product as ActivityProduct, policies);
        default:
            return [];
    }
}

function getHotelItems(
    product: HotelProduct,
    policies: BookingCancellationPolicy[]
): BookingCancellationItem[] {
    const result: BookingCancellationItem[] = [];
    product.rooms.forEach((room) => {
        const roomPolicies = getRoomPolicies(room, policies);
        result.push({
            refundable:
                roomPolicies.length > 0
                    ? roomPolicies[0].refundable && roomPolicies[0].active
                    : undefined,
            itemId: String(room.productId),
            description: room.roomDescription,
            policies: roomPolicies,
        });
    });
    return result;
}

function getActivityItems(
    product: ActivityProduct,
    policies: BookingCancellationPolicy[]
): BookingCancellationItem[] {
    const result: BookingCancellationItem[] = [];
    product.tickets.forEach((ticket, index) => {
        const ticketPolicies = getTicketPolicies(ticket, policies);
        result.push({
            refundable:
                ticketPolicies.length > 0
                    ? ticketPolicies[0].refundable && ticketPolicies[0].active
                    : undefined,
            itemId: String(ticket.productId),
            description: `ticket ${index + 1}`,
            policies: ticketPolicies,
        });
    });
    return result;
}

function getRoomPolicies(
    room: HotelRoom,
    policies: BookingCancellationPolicy[]
): BookingItemCancellationPolicy[] {
    const roomPolicies = policies
        .filter((x: { productId: number }) => x.productId === room.productId)
        .sort(dateComparer);

    if (isEmpty(roomPolicies)) {
        handleCustomError(`No cancellation policies found for room ID ${room.productId}`);
        return [];
    }

    const mappedPolicies = mapPolicies(roomPolicies, room.productId);

    const zeroAmountPolicy = addZeroAmountPolicy(mappedPolicies);
    if (zeroAmountPolicy) mappedPolicies.unshift(zeroAmountPolicy);

    return mappedPolicies;
}

function getTicketPolicies(
    ticket: ActivityTicket,
    policies: BookingCancellationPolicy[]
): BookingItemCancellationPolicy[] {
    const ticketPolicies = policies
        .filter((x: { productId: number }) => x.productId === ticket.productId)
        .sort(dateComparer);

    if (isEmpty(ticketPolicies)) {
        handleCustomError(`No cancellation policies found for ticket ID ${ticket.productId}`);
        return [];
    }

    const mappedPolicies = mapPolicies(ticketPolicies, ticket.productId);

    const zeroAmountPolicy = addZeroAmountPolicy(mappedPolicies);
    if (zeroAmountPolicy) mappedPolicies.unshift(zeroAmountPolicy);

    return mappedPolicies;
}

export function getFirstAmountBookingPolicy(
    cancellationProduct: BookingProductCancellation
): BookingItemCancellationPolicy | undefined {
    const policies: BookingItemCancellationPolicy[] = cancellationProduct.items
        .flatMap((item) => item.policies)
        .filter((x) => x.refundable && x.active);
    return sortBookingCancellationPolicies(policies)[0];
}

function addZeroAmountPolicy(
    policies: BookingItemCancellationPolicy[]
): BookingItemCancellationPolicy | undefined {
    if (isEmpty(policies)) return undefined;
    const first = policies[0];
    if (first.amount.Amount > 0) {
        const today = getToday();
        if (isAfter(today, convertToDate(first.fromDate))) return undefined;
        const hours = getDurationInHours(convertToDate(first.fromDate), today, false);
        return {
            id: first.id,
            refundable: true,
            active: hours > 0,
            amount: {
                Amount: 0,
                Currency: first.amount.Currency,
            },
            fromDate: today,
            endDate: first.fromDate,
        };
    }
    return undefined;
}

function mapPolicies(
    policies: BookingCancellationPolicy[],
    id: number
): BookingItemCancellationPolicy[] {
    return policies.map((policy: BookingCancellationPolicy, index) => {
        const today = getToday();
        const nextPolicy = index < policies.length ? policies[index + 1] : undefined;
        const endDate = nextPolicy ? nextPolicy.fromDate : undefined;
        const active = endDate
            ? getDurationInHours(convertToDate(endDate), today, false) > 0
            : true;

        return {
            id,
            refundable: policy.price === 0,
            fromDate: policy.fromDate,
            active,
            endDate,
            amount: {
                Amount: policy.price,
                Currency: policy.currency,
            },
        };
    });
}

function sortBookingCancellationPolicies(
    policies: BookingItemCancellationPolicy[]
): BookingItemCancellationPolicy[] {
    if (policies.length === 0) return policies;

    const dateComparer = compare<BookingItemCancellationPolicy>(
        onDate((policy) => convertToDate(policy.fromDate))
    );

    return policies.sort(dateComparer);
}
